import { Button, Form, Input, Space } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { useOnFormClose, useGenericFormErrorNotification } from "@/hooks/forms";
import { useChain } from "@/hooks/useChain";
import DocumentUploadField, {
  getSingleDocumentUploadFieldValue,
} from "@/modules/document/document-upload-field";
import { useFormValidation } from "@/modules/form-validation";
import {
  getInterventionTemplateId,
  InterventionTemplateSelect,
} from "@/modules/intervention-template/intervention-template-select";
import { SidePanelFooter } from "@/modules/side-panel";

import { InitialValuesType, ModelType } from "./OrganizationForm.type";

const { useForm } = Form;

const OrganizationForm = ({
  model,
  mutation,
  saveButtonLabel,
  initialValues,
  onMutate,
}) => {
  const [form] = useForm();
  const { t } = useTranslation();

  const onClose = useOnFormClose(form);
  const onMutateSuccess = useChain([onClose, onMutate]);

  useGenericFormErrorNotification(mutation.isError);
  const { validationErrors, validation } = useFormValidation(
    mutation.isError,
    mutation.error,
    [
      "name",
      "description",
      "address",
      "logo_document",
      "intervention_template_id",
    ]
  );

  const onSave = (formValues) => {
    const data = {
      name: form.getFieldValue("name"),
      description: form.getFieldValue("description"),
      address: form.getFieldValue("address"),
      logo_document: getSingleDocumentUploadFieldValue({
        formValue: formValues.logo_document,
        defaultDocument: model?.logo_document,
      }),
    };

    data["intervention_template_id"] = getInterventionTemplateId(
      form.getFieldValue("template")
    );

    mutation.mutate(data, { onSuccess: onMutateSuccess });
  };

  return (
    <Form
      id="organizationForm"
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onFinish={onSave}
      role="form"
    >
      <Form.Item
        name="name"
        label={t("organization:form.name.label")}
        rules={[{ required: true }]}
        {...validation("name")}
      >
        <Input
          placeholder={t("organization:form.name.label")}
          data-testid="name"
        />
      </Form.Item>

      <Form.Item
        name="description"
        label={t("organization:form.description.label")}
        {...validation("description")}
      >
        <Input
          placeholder={t("organization:form.description.label")}
          data-testid="description"
        />
      </Form.Item>

      <Form.Item
        name="address"
        label={t("organization:form.address.label")}
        {...validation("address")}
      >
        <Input
          placeholder={t("organization:form.address.label")}
          data-testid="address"
        />
      </Form.Item>

      <DocumentUploadField
        name="logo_document"
        label={t("organization:form.image.label")}
        document={model?.logo_document}
        uploadProps={{ type: "image", translationType: "photo" }}
        {...validation("logo_document")}
      />

      <InterventionTemplateSelect
        name="template"
        {...validation("intervention_template_id")}
      />

      {validationErrors().others && <Form.Item {...validation("others")} />}

      <SidePanelFooter>
        <Space>
          <Button onClick={onClose}>{t("global:cancel")}</Button>
          <Button
            loading={mutation.isPending}
            type="primary"
            htmlType="submit"
            form="organizationForm"
          >
            {saveButtonLabel}
          </Button>
        </Space>
      </SidePanelFooter>
    </Form>
  );
};

OrganizationForm.propTypes = {
  model: PropTypes.shape(ModelType),
  mutation: PropTypes.object.isRequired,
  saveButtonLabel: PropTypes.string.isRequired,
  initialValues: PropTypes.shape(InitialValuesType),
  onMutate: PropTypes.func,
};

export default OrganizationForm;
